import { useMemo, useState } from 'react';
import { barPath, niceTicks, useMeasure, type Series } from './chartUtils';

export interface BarChartProps<T extends Record<string, unknown>> {
  data: T[];
  xKey: keyof T & string;
  series: Series[];
  height?: number;
  yFormat?: (v: number) => string;
  xFormat?: (v: string) => string;
  /** Segments sit on top of each other with a 2px surface gap between them. */
  stacked?: boolean;
  target?: { value: number; label: string };
  emptyMessage?: string;
  /** Makes each column clickable — for a drill-down into the period it shows. */
  onSelect?: (row: T, index: number) => void;
}

const PAD = { top: 12, right: 16, bottom: 26, left: 48 };
/** The surface gap between adjacent fills, so two segments read as two. */
const GAP = 2;

/**
 * A grouped or stacked bar chart.
 *
 * Always zero-based, unlike LineChart: a bar's LENGTH is its value, so a
 * truncated axis makes the chart lie about ratios in a way a line chart does
 * not. There is no option to turn that off.
 */
export function BarChart<T extends Record<string, unknown>>({
  data,
  xKey,
  series,
  height = 220,
  yFormat = (v) => String(Math.round(v)),
  xFormat = (v) => v,
  stacked = false,
  target,
  emptyMessage = 'No data for this period.',
  onSelect,
}: BarChartProps<T>) {
  const [ref, width] = useMeasure<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);

  const geo = useMemo(() => {
    const w = Math.max(width, 200);
    const innerW = Math.max(w - PAD.left - PAD.right, 10);
    const innerH = Math.max(height - PAD.top - PAD.bottom, 10);

    /* Stacked scales to the column total; grouped to the tallest single bar. */
    const totals = data.map((row) =>
      stacked
        ? series.reduce((sum, s) => sum + (Number(row[s.key]) || 0), 0)
        : Math.max(...series.map((s) => Number(row[s.key]) || 0)),
    );
    const max = Math.max(...totals, target?.value ?? 0, 1);
    const ticks = niceTicks(0, max, 4);
    const yMax = Math.max(...ticks);

    const bandW = innerW / Math.max(data.length, 1);
    /* Capped at 48px so six bars do not become six slabs, and floored at 3px so
       a year of daily data still draws something. */
    const barW = Math.max(3, Math.min(bandW * 0.62, 48));
    const groupW = stacked ? barW : Math.max(3, (barW - GAP * (series.length - 1)) / series.length);

    const y = (v: number) => PAD.top + innerH - (v / yMax) * innerH;
    const bandX = (i: number) => PAD.left + i * bandW;

    return { w, innerW, innerH, ticks, yMax, bandW, barW, groupW, y, bandX, baseline: PAD.top + innerH };
  }, [width, height, data, series, stacked, target]);

  if (!data.length) return <div className="empty">{emptyMessage}</div>;

  const tickEvery = Math.max(1, Math.ceil(data.length / Math.max(2, Math.floor(geo.innerW / 68))));
  const hovered = hover !== null ? data[hover] : null;

  return (
    <div className="chart-frame" ref={ref}>
      <svg
        className="chart"
        width={geo.w}
        height={height}
        role="img"
        aria-label={`Bar chart: ${series.map((s) => s.label).join(', ')}`}
        onMouseLeave={() => setHover(null)}
      >
        {geo.ticks.map((t) => (
          <g key={t}>
            <line className="chart-grid" x1={PAD.left} x2={geo.w - PAD.right} y1={geo.y(t)} y2={geo.y(t)} />
            <text className="chart-tick" x={PAD.left - 8} y={geo.y(t)} textAnchor="end" dominantBaseline="middle">
              {yFormat(t)}
            </text>
          </g>
        ))}

        <line className="chart-axis" x1={PAD.left} x2={geo.w - PAD.right} y1={geo.baseline} y2={geo.baseline} />

        {data.map((row, i) => {
          const centre = geo.bandX(i) + geo.bandW / 2;
          /* Walks up the column as each segment is placed. */
          let stackTop = geo.baseline;
          return (
            <g
              key={`bar-${i}`}
              onMouseEnter={() => setHover(i)}
              onClick={onSelect ? () => onSelect(row, i) : undefined}
              style={onSelect ? { cursor: 'pointer' } : undefined}
            >
              {/* A full-height hit target, so hovering the gap above a short bar
                  still selects its column. */}
              <rect x={geo.bandX(i)} y={PAD.top} width={geo.bandW} height={geo.innerH} fill="transparent" />
              {series.map((s, si) => {
                const value = Number(row[s.key]) || 0;
                const rawH = (value / geo.yMax) * geo.innerH;

                if (stacked) {
                  /* The gap is taken out of the drawn height but NOT out of the
                     running total, so the segments stay in proportion. */
                  const h = Math.max(rawH - (si > 0 ? GAP : 0), 0);
                  const yTop = stackTop - h;
                  stackTop -= rawH;
                  return h > 0 ? (
                    <path
                      key={s.key}
                      /* Only the topmost segment is rounded — an inner segment
                         with rounded corners reads as a separate bar. */
                      d={barPath(centre - geo.barW / 2, yTop, geo.barW, h, 'top', si === series.length - 1 ? 4 : 0)}
                      opacity={hover === null || hover === i ? 1 : 0.42}
                      style={{ fill: s.color }}
                    />
                  ) : null;
                }

                const x = centre - geo.barW / 2 + si * (geo.groupW + GAP);
                return rawH > 0 ? (
                  <path
                    key={s.key}
                    d={barPath(x, geo.baseline - rawH, geo.groupW, rawH, 'top', 4)}
                    opacity={hover === null || hover === i ? 1 : 0.42}
                    style={{ fill: s.color }}
                  />
                ) : null;
              })}
            </g>
          );
        })}

        {target && (
          <>
            <line
              className="chart-target"
              x1={PAD.left}
              x2={geo.w - PAD.right}
              y1={geo.y(target.value)}
              y2={geo.y(target.value)}
            />
            <text className="chart-tick" x={geo.w - PAD.right} y={geo.y(target.value) - 5} textAnchor="end">
              {target.label}
            </text>
          </>
        )}

        {data.map((row, i) =>
          i % tickEvery === 0 || i === data.length - 1 ? (
            <text
              key={`t-${i}`}
              className="chart-tick"
              x={geo.bandX(i) + geo.bandW / 2}
              y={height - 8}
              textAnchor="middle"
            >
              {xFormat(String(row[xKey]))}
            </text>
          ) : null,
        )}
      </svg>

      {hover !== null && hovered && (
        <div
          className="chart-tooltip"
          style={{
            left: Math.min(Math.max(geo.bandX(hover) + geo.bandW / 2, 76), geo.w - 76),
            top: PAD.top + 2,
          }}
        >
          <div className="chart-tt-title">{xFormat(String(hovered[xKey]))}</div>
          {series.map((s) => (
            <div className="chart-tt-row" key={s.key}>
              <span className="chart-swatch" style={{ background: s.color }} />
              <span>{s.label}</span>
              <span className="chart-tt-value">{yFormat(Number(hovered[s.key]) || 0)}</span>
            </div>
          ))}
          {stacked && series.length > 1 && (
            <div className="chart-tt-row chart-tt-total">
              <span>Total</span>
              <span className="chart-tt-value">
                {yFormat(series.reduce((sum, s) => sum + (Number(hovered[s.key]) || 0), 0))}
              </span>
            </div>
          )}
        </div>
      )}

      {series.length > 1 && (
        <div className="chart-legend">
          {series.map((s) => (
            <span className="chart-legend-item" key={s.key}>
              <span className="chart-legend-swatch" style={{ background: s.color }} />
              {s.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
